// src/features/template/RewriteTemplate.tsx

import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Collapse
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { TemplateForm } from './components/TemplateForm';
import { AllowedDomains } from './components/AllowedDomains';
import { BiasesPanel } from './components/BiasesPanel';
import { RichTextEditor } from './components/RichTextEditor';
import { useTemplateForm } from './hooks/useTemplateForm';
import { TemplateVariation } from './types/index';
import { rewriteSchema } from './validation/schemas';
import { ProcessingProvider, useProcessing } from '../../features/processing/context';
import { ProcessingContent } from '../../features/processing/components/ProcessingContent';
import { WebSocketProvider } from '../../api/websocket/WebSocketContext';
import { ModelSelector } from './components/ModelSelector';
import { TemplateApi } from './api/templateApi';

export const RewriteTemplate: React.FC<{ onWsUrlChange: (url: string | null) => void }> = ({ onWsUrlChange }) => {
  const navigate = useNavigate();
  const [showProcessing, setShowProcessing] = useState(false);
  const [processSessionId, setProcessSessionId] = useState(0);
  const [models, setModels] = useState<string[]>([]);
  const [loadingModels, setLoadingModels] = useState(false);
  const {
    form,
    errors,
    handleChange,
    validateForm,
    setForm
  } = useTemplateForm(rewriteSchema);

  const [rewrittenTemplates, setRewrittenTemplates] = useState<TemplateVariation[]>([]);
  const [showResults, setShowResults] = useState(false);

  // Memoize form to prevent unnecessary re-renders
  const memoizedForm = React.useMemo(() => ({
    from: form.from,
    subject: form.subject,
    body: form.body,
    allowedDomains: form.allowedDomains,
    senderBias: form.senderBias,
    contentBiases: form.contentBiases,
    selectedModel: form.selectedModel
  }), [form.from, form.subject, form.body, form.allowedDomains, form.senderBias, form.contentBiases, form.selectedModel]);

  const { state: processingState, dispatch } = useProcessing();
  const processingStateRef = useRef(processingState);

  // Keep the ref updated with the latest state
  useEffect(() => {
    processingStateRef.current = processingState;
  }, [processingState]);

  // Fetch available models on component mount
  useEffect(() => {
    const fetchModels = async () => {
      setLoadingModels(true);
      try {
        const response = await TemplateApi.getModels();
        setModels(response.models);
      } catch (error) {
        console.error('Error fetching models:', error);
        // Silently fail and don't show the model selector
        setModels([]);
      } finally {
        setLoadingModels(false);
      }
    };

    fetchModels();
  }, []);

  // Handle processing completion
  const handleProcessingComplete = useCallback(() => {
    // Use the ref to get the latest state
    const currentState = processingStateRef.current;
    console.log('[RewriteTemplate] Processing complete', { result: currentState.result });
    
    // Check if we have results in the processing state
    if (currentState.result && 'variations' in currentState.result) {
      console.log('[RewriteTemplate] Found variations in result', currentState.result.variations);
      setRewrittenTemplates(currentState.result.variations);
      setShowResults(true);
      setShowProcessing(false); // Re-enable the submit button and hide processing UI
    } else {
      // For error case, just re-enable the submit button but keep processing content visible
      console.log('[RewriteTemplate] No variations in result, keeping processing content visible');
      setShowProcessing(false); // Re-enable the submit button
    }
  }, []); // No dependencies needed since we use the ref

  const handleSubmit = async () => {
    if (validateForm()) {
      console.log('[RewriteTemplate] Dispatching RESET_STATE');
      dispatch({ type: 'RESET_STATE' });
      setShowResults(false);
      console.log('[RewriteTemplate] Updating processSessionId');
      setProcessSessionId((prev) => {
        const newId = prev + 1;
        console.log('[RewriteTemplate] processSessionId updated to', newId);
        return newId;
      });
      console.log('[RewriteTemplate] Setting showProcessing to true');
      setShowProcessing(true);
    }
  };

  const handleCopyTemplate = async (template: TemplateVariation) => {
    try {
      const templateText = `From: ${template.from_address}
Subject: ${template.subject}

${template.body}`;
      
      await navigator.clipboard.writeText(templateText);
    } catch (error) {
      console.error('Copy error:', error);
    }
  };

  return (
    <Box sx={{ 
      display: 'flex',
      flexGrow: 1,
      bgcolor: '#1B2B3A',
      p: 2
    }}>
      <Box sx={{ flex: 1, p: 4, maxWidth: 800, mx: 'auto' }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/dashboard')}
          sx={{ 
            mb: 4, 
            color: 'white',
            textTransform: 'none',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.08)'
            }
          }}
        >
          Back to Dashboard
        </Button>

        <Paper 
          elevation={0}
          sx={{ 
            p: 4,
            backgroundColor: '#243B53',
            borderRadius: '12px',
            color: 'white'
          }}
        >
          <Typography variant="h5" sx={{ mb: 4, color: 'white', fontWeight: 500 }}>
            Rewrite Template
          </Typography>
          
          <TemplateForm
            form={form}
            errors={errors}
            onChange={handleChange}
          />

          <AllowedDomains
            selectedDomains={form.allowedDomains}
            onChange={(domains) => handleChange('allowedDomains', domains)}
            error={errors.allowedDomains}
          />

          <RichTextEditor
            value={form.body}
            onChange={(value) => handleChange('body', value)}
            error={errors.body}
          />

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Button 
              variant="contained" 
              size="large"
              onClick={handleSubmit}
              disabled={showProcessing}
              sx={{ 
                px: 4,
                py: 1.5,
                backgroundColor: '#B4E33D',
                color: '#1B2B3A',
                borderRadius: '25px',
                textTransform: 'none',
                fontWeight: 500,
                '&:hover': {
                  backgroundColor: '#9BC535'
                }
              }}
            >
              {showProcessing ? 'Rewriting...' : 'Rewrite Template'}
            </Button>

            <ModelSelector
              models={models}
              selectedModel={form.selectedModel}
              onChange={(model) => handleChange('selectedModel', model)}
              loading={loadingModels}
            />
          </Box>

        </Paper>

        {(showProcessing || Object.keys(processingState.stages).length > 0) && (
          <Paper 
            elevation={0}
            sx={{ 
              mt: 2,
              backgroundColor: 'transparent',
              color: 'white'
            }}
          >
            <ProcessingContent
              key={processSessionId}
              form={memoizedForm}
              processingType="rewrite"
              onComplete={handleProcessingComplete}
              onWsUrlChange={onWsUrlChange}
            />
          </Paper>
        )}

        <Collapse in={showResults}>
          {rewrittenTemplates.map((template, index) => (
            <Paper
              key={index}
              elevation={0}
              sx={{ 
                mt: 3,
                p: 4,
                backgroundColor: '#243B53',
                borderRadius: '12px',
                color: 'white'
              }}
            >
              <Typography variant="h6" sx={{ mb: 2, color: 'white', fontWeight: 500 }}>
                Variation {index + 1}
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  From:
                </Typography>
                <Typography sx={{ color: 'white' }}>{template.from_address}</Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  Subject:
                </Typography>
                <Typography sx={{ color: 'white' }}>{template.subject}</Typography>
              </Box>
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  Body:
                </Typography>
                <Box 
                  dangerouslySetInnerHTML={{ __html: template.body }}
                  sx={{ 
                    backgroundColor: 'rgba(27, 43, 58, 0.5)',
                    p: 2,
                    borderRadius: '8px',
                    mt: 1,
                    color: 'white'
                  }}
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => handleCopyTemplate(template)}
                  sx={{
                    color: '#00A4E0',
                    borderColor: '#00A4E0',
                    textTransform: 'none',
                    '&:hover': {
                      borderColor: '#00A4E0',
                      backgroundColor: 'rgba(0, 164, 224, 0.1)'
                    }
                  }}
                >
                  Copy to Clipboard
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setForm({
                      ...form,
                      from: template.from_address,
                      subject: template.subject,
                      body: template.body
                    });
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  sx={{
                    color: '#B4E33D',
                    borderColor: '#B4E33D',
                    textTransform: 'none',
                    '&:hover': {
                      borderColor: '#B4E33D',
                      backgroundColor: 'rgba(180, 227, 61, 0.1)'
                    }
                  }}
                >
                  Edit This Version
                </Button>
              </Box>
            </Paper>
          ))}
        </Collapse>
      </Box>

      <BiasesPanel
        senderBias={form.senderBias}
        contentBiases={form.contentBiases}
        onSenderBiasChange={(bias) => handleChange('senderBias', bias)}
        onContentBiasChange={(biases) => handleChange('contentBiases', biases)}
        errors={{
          senderBias: errors.senderBias,
          contentBiases: errors.contentBiases
        }}
      />
    </Box>
  );
};

// Wrap the component with ProcessingProvider and WebSocketProvider
export default function RewriteTemplateWithProvider() {
  const [wsUrl, setWsUrl] = useState<string | null>(null);

  return (
    <WebSocketProvider wsUrl={wsUrl || ''}>
      <ProcessingProvider initialProcessingType="rewrite">
        <RewriteTemplate onWsUrlChange={setWsUrl} />
      </ProcessingProvider>
    </WebSocketProvider>
  );
}
